<template>
  <nav class="menu-bar">
    <div
      v-for="menu in menus"
      :key="menu.label"
      class="menu"
      @mouseenter="openMenu(menu.label)"
      @mouseleave="closeMenu"
    >
      <button
        class="menu-button"
        type="button"
        :data-menu="menu.label.toLowerCase()"
        :data-tutorial="menu.label === 'File' ? 'file-menu' : menu.label === 'Help' ? 'help-menu' : undefined"
      >
        {{ menu.label }}
      </button>

      <ul v-if="activeMenu === menu.label" class="dropdown">
        <li
          v-for="item in menu.items"
          :key="item.key"
          :class="{ separator: item.type === 'separator', 'submenu-host': Boolean(item.children) }"
          :role="item.type === 'separator' ? 'separator' : undefined"
          @mouseleave="item.children && onSubmenuHostLeave($event)"
          @keydown.esc="item.children && onSubmenuEscape()"
        >
          <template v-if="item.children">
            <button
              :ref="setExportButtonRef"
              class="submenu-button"
              type="button"
              :data-submenu="item.key"
              aria-haspopup="menu"
              :aria-expanded="activeSubmenu === item.key ? 'true' : 'false'"
              @mouseenter="openSubmenu(item.key)"
              @focus="openSubmenu(item.key)"
              @click="toggleSubmenu(item.key)"
            >
              {{ item.label }}
            </button>
            <ul v-if="activeSubmenu === item.key" class="submenu" role="menu">
              <li v-for="child in item.children" :key="child.key" role="none">
                <button
                  class="menu-item-button"
                  type="button"
                  role="menuitem"
                  :data-action="child.key"
                  @click="onItemClick(menu, child)"
                >
                  {{ child.label }}
                </button>
              </li>
            </ul>
          </template>
          <button
            v-else-if="item.type !== 'separator'"
            class="menu-item-button"
            type="button"
            :data-action="item.key"
            @click="onItemClick(menu, item)"
          >
            {{ item.label }}
          </button>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script setup>
import { ref } from "vue"

const emit = defineEmits(["menu-action"])

const activeMenu = ref(null)
const activeSubmenu = ref(null)
const exportButtonRef = ref(null)

function setExportButtonRef(el) {
  exportButtonRef.value = el
}

function openMenu(label) {
  activeMenu.value = label
}

function closeMenu() {
  activeMenu.value = null
  activeSubmenu.value = null
}

function openSubmenu(key) {
  activeSubmenu.value = key
}

function closeSubmenu({ restoreFocus = false } = {}) {
  activeSubmenu.value = null
  if (restoreFocus) exportButtonRef.value?.focus()
}

function toggleSubmenu(key) {
  if (activeSubmenu.value === key) closeSubmenu()
  else openSubmenu(key)
}

function onSubmenuEscape() {
  closeSubmenu({ restoreFocus: true })
}

function onSubmenuHostLeave(event) {
  const next = event.relatedTarget
  if (next && event.currentTarget.contains(next)) return
  closeSubmenu()
}

function onItemClick(menu, item) {
  if (item.type === "separator" || item.children) return

  // close dropdown immediately
  closeMenu()

  // emit a single, consistent event payload
  emit("menu-action", {
    menu: menu.label,     // e.g. "File"
    action: item.key,     // e.g. "file:new"
    label: item.label     // e.g. "New"
  })
}

const menus = [
  {
    label: "File",
    items: [
      { key: "file:new", label: "New" },
      { key: "file:open", label: "Open" },
      { key: "file:import-features", label: "Import Features…" },
      { key: "file:import-export-separator", type: "separator" },
      {
        key: "file:export",
        label: "Export",
        children: [
          { key: "file:export-kml", label: "To KML" },
          { key: "file:export-geojson", label: "To GeoJSON" },
          { key: "file:export-pdf", label: "To PDF" },
          { key: "file:export-csv", label: "To CSV" },
        ],
      },
    ]
  },
  {
    label: "Tools",
    items: [
      { key: "tools:settings", label: "Settings…" },
      { key: "tools:settings-separator", type: "separator" },
      { key: "tools:add-vertices", label: "Add Vertices" },
      { key: "tools:remove-duplicate-points", label: "Remove Duplicate Points" },
      { key: "tools:coordinate-converter", label: "Coordinate Converter" },
    ]
  },
  {
    label: "Help",
    items: [
      { key: "help:tutorial", label: "Tutorial" },
      { key: "help:about", label: "About" },
      { key: "help:load-demo-kml", label: "Load Demo KML" },
    ]
  }
]
</script>

<style scoped>
.menu-bar {
  position: sticky;
  top: var(--header-height);
  z-index: 950;
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-4);
  background: var(--color-bg-subtle);
  border-bottom: 1px solid var(--color-border);
  font-family: var(--font-sans);
}

.menu {
  position: relative;
}

/* Invisible bridge: fills the gap between the button and the dropdown so the
   cursor never leaves the .menu hover region on its way down to an item.
   Only present while this menu's dropdown is open. */
.menu:hover::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: var(--space-2);
}

.menu-button {
  background: none;
  border: none;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.menu-button:hover {
  background: var(--color-bg-raised);
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  background: var(--color-bg-raised);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  list-style: none;
  padding: var(--space-1) 0;
  margin: var(--space-2) 0 0;
  z-index: 960;
}

.dropdown li {
  font-size: var(--fs-sm);
  white-space: nowrap;
}

.menu-item-button,
.submenu-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
  background: none;
  border: none;
  color: inherit;
  font-family: inherit;
  font-size: var(--fs-sm);
  text-align: left;
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  white-space: nowrap;
}

.menu-item-button:hover,
.menu-item-button:focus-visible,
.submenu-button:hover,
.submenu-button:focus-visible {
  background: var(--color-bg-subtle);
}

.submenu-button::after {
  content: "▸";
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.submenu-host {
  position: relative;
}

/* Invisible bridge: covers the horizontal offset between the Export parent and
   its child panel so the pointer never leaves the host on the way across. */
.submenu-host::after {
  content: "";
  position: absolute;
  top: 0;
  left: 100%;
  width: var(--space-1);
  height: 100%;
}

.submenu {
  position: absolute;
  left: 100%;
  top: calc(-1 * var(--space-1));
  margin: 0 0 0 var(--space-1);
  min-width: 160px;
  background: var(--color-bg-raised);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  list-style: none;
  padding: var(--space-1) 0;
  z-index: 970;
}

.dropdown li.separator {
  margin: var(--space-1) var(--space-3);
  padding: 0;
  border-top: 1px solid var(--color-border);
  cursor: default;
}

.dropdown li.separator:hover {
  background: transparent;
}
</style>
